import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';

const sensitivityLabel = {
  normal: '일반',
  caution: '주의',
  sensitive: '민감',
  restricted: '제한',
};

// 위젯 색상 swatch — widgetAccent.js 의 10색과 일치
const ACCENT_OPTIONS = [
  { key: 'emerald', dot: 'bg-emerald-400 border-emerald-500', label: '에메랄드' },
  { key: 'orange',  dot: 'bg-orange-400 border-orange-500',   label: '오렌지' },
  { key: 'indigo',  dot: 'bg-indigo-400 border-indigo-500',   label: '인디고' },
  { key: 'amber',   dot: 'bg-amber-400 border-amber-500',     label: '앰버' },
  { key: 'blue',    dot: 'bg-blue-400 border-blue-500',       label: '블루' },
  { key: 'pink',    dot: 'bg-pink-400 border-pink-500',       label: '핑크' },
  { key: 'violet',  dot: 'bg-violet-400 border-violet-500',   label: '바이올렛' },
  { key: 'sky',     dot: 'bg-sky-400 border-sky-500',         label: '스카이' },
  { key: 'rose',    dot: 'bg-rose-400 border-rose-500',       label: '로즈' },
  { key: 'slate',   dot: 'bg-slate-400 border-slate-500',     label: '슬레이트' },
];

export function SituationWidgetSettingsPanel({
  open,
  widgets,
  hiddenSet,
  accentMap = {},
  onToggleHidden,
  onChangeAccent,
  onClose,
}) {
  useBodyScrollLock(open);
  // 어떤 위젯의 색상 picker 가 열려있는지 (code 또는 null)
  const [openAccentFor, setOpenAccentFor] = useState(null);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] bg-black/35 flex items-start justify-center px-4 py-8">
      <div className="w-full max-w-2xl max-h-[82vh] overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] shadow-card-lg">
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between gap-3">
          <div>
            <h3 className="m-0 text-base font-black text-[var(--text)]">상황판 위젯</h3>
            <p className="mt-1 text-xs text-[var(--text-sub)]">배정된 위젯의 색상을 바꾸거나 필요 없는 항목을 숨길 수 있습니다.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-[var(--border)] text-xs font-bold text-[var(--text-sub)] hover:bg-[var(--bg-subtle)]"
          >
            닫기
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(82vh-76px)] grid gap-2">
          {widgets.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[var(--border)] p-5 text-sm text-[var(--text-sub)]">
              표시 가능한 위젯이 없습니다.
            </div>
          ) : widgets.map((widget) => {
            const hidden = hiddenSet.has(widget.code);
            const sources = Array.isArray(widget.data_sources) ? widget.data_sources : [];
            const accentOpen = openAccentFor === widget.code;
            const currentAccent = accentMap[widget.code] || null;
            return (
              <div
                key={widget.code}
                className={cn(
                  'rounded-xl border border-[var(--border)] bg-[var(--bg)] px-4 py-3',
                  hidden && 'opacity-65'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-black text-[var(--text)]">{widget.name}</span>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--bg-subtle)] text-[var(--text-sub)]">
                        {sensitivityLabel[widget.sensitivity] || widget.sensitivity}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[var(--text-sub)]">{widget.description}</p>
                    {sources.length > 0 && (
                      <p className="mt-1 text-[11px] text-[var(--text-muted)]">
                        데이터 출처: {sources.join(' / ')}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setOpenAccentFor(accentOpen ? null : widget.code)}
                      title="색상 변경"
                      className={cn(
                        'shrink-0 px-2.5 py-1.5 rounded-lg border text-xs font-black flex items-center gap-1.5',
                        'border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-sub)] hover:bg-[var(--bg-subtle)]'
                      )}
                    >
                      <span className={cn(
                        'w-3.5 h-3.5 rounded-full border',
                        currentAccent
                          ? (ACCENT_OPTIONS.find((o) => o.key === currentAccent)?.dot || 'bg-slate-300 border-slate-400')
                          : 'bg-gradient-to-br from-emerald-400 via-blue-400 to-pink-400 border-slate-300'
                      )} />
                      색상
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleHidden(widget.code)}
                      className={cn(
                        'shrink-0 px-3 py-1.5 rounded-lg border text-xs font-black',
                        hidden
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300'
                          : 'border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-sub)] hover:bg-[var(--bg-subtle)]'
                      )}
                    >
                      {hidden ? '다시 표시' : '숨기기'}
                    </button>
                  </div>
                </div>
                {accentOpen && (
                  <div className="mt-3 pt-3 border-t border-[var(--border)]">
                    <div className="flex items-center gap-2 flex-wrap">
                      {ACCENT_OPTIONS.map((opt) => {
                        const selected = currentAccent === opt.key;
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => {
                              onChangeAccent?.(widget.code, opt.key);
                              setOpenAccentFor(null);
                            }}
                            title={opt.label}
                            className={cn(
                              'w-7 h-7 rounded-full border-2 transition-transform hover:scale-110',
                              opt.dot,
                              selected && 'ring-2 ring-offset-2 ring-[var(--text)] ring-offset-[var(--bg-card)]'
                            )}
                          />
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          onChangeAccent?.(widget.code, null);
                          setOpenAccentFor(null);
                        }}
                        className="ml-1 px-2.5 py-1 rounded-md text-[11px] font-bold border border-[var(--border)] text-[var(--text-sub)] hover:bg-[var(--bg-subtle)]"
                      >
                        기본값
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
